import { Dispatch } from "@reduxjs/toolkit";
import { benefitSelectionTypes } from "constants/benefitSelection";
import { getRandomNumber } from "helpers/number";
import { filterUniqueObjects } from "helpers/object";
import { isEmpty, orderBy, times } from "lodash";
import { pickDefaultPayoutProvider } from "pages/CreateOrder/utils/payoutProviderSetup";
import { setPriceBreakdownRows } from "store/slices/serviceDetails";
import {
  PriceTableBreakdown,
  PricingBreakdown,
  ProcedureRow,
  ProviderDropdown,
} from "types/CreateOrder";
import { IPriorAuthFinding } from "types/PriorAuth/PriorAuth";
import { IProcedure } from "types/Procedure";
import { SpecialtyList } from "types/Provider/Provider";
import { IProviderProcedure } from "types/ProviderProcedure";

import { ProcedureInfoColumn } from "./components/Common/ProcedureInfoColumn";
import {
  getNextInternalId,
  getProcedureId,
  getProcedureProvidersWithSameSpecialty,
  getProviderProceduresInfo,
  isSpecialtyAlreadyAdded,
  isValidPrice,
  uniqueByField,
} from "./helpers";
import { ISelectedProcedure } from "./types";

// Sums `amount` — the price each row displays and persists to
// `serviceDetails.procedures[].amountInCents` — and NOT
// `selectedProvider.priceInCents`, which is the provider's price as it stands in
// the fee schedule right now. The two are identical on a fresh estimate, but
// CopyOrder/RedoOrder/ReviewOrder rehydrate `amount` from the source order while
// leaving `selectedProvider` on today's price. Summing the live price there gave
// the estimate a total its own line items did not add up to (CLE-2775).
export const buildClearPackageTotalAmount = (rows: ProcedureRow[]): number => {
  return rows.reduce(
    (total: number, { amount }: ProcedureRow) => total + amount,
    0
  );
};

export const buildGFETotalAmount = (rows: PriceTableBreakdown[]): number => {
  return rows.reduce(
    (total: number, { amount }: PriceTableBreakdown) => total + amount,
    0
  );
};

export const buildInternalRowsQuantity = (rows: ProcedureRow[]) => {
  const rowsWithQuantityForTableDisplay = rows.reduce(
    (acc: ProcedureRow[], curr: ProcedureRow) => {
      const existingItemIndex = acc.findIndex(
        (item) =>
          item.selectedProvider.id === curr.selectedProvider.id &&
          item.cptCode === curr.cptCode
      );

      if (existingItemIndex !== -1) {
        acc[existingItemIndex] = {
          ...acc[existingItemIndex],
          quantity: (acc[existingItemIndex].quantity || 0) + 1,
        };
      } else {
        acc.push({ ...curr, quantity: 1 });
      }

      return acc;
    },
    []
  );
  return orderBy(
    rowsWithQuantityForTableDisplay,
    ["cptCode", "internalId", "specialty"],
    ["asc", "asc", "asc"]
  );
};

export const buildSelectedProceduresToTable = ({
  selectedProceduresObject,
  procedures,
  providerProcedures,
  rows,
  row,
  originalProcedures,
  isFinixAccount = false,
}: {
  selectedProceduresObject: ISelectedProcedure[];
  procedures: IProcedure[];
  providerProcedures: IProviderProcedure[];
  rows: ProcedureRow[];
  row?: ProcedureRow;
  originalProcedures?: any[];
  /** CLE-3074 — start a new row on a provider whose Finix payment setup is complete, when there is one. */
  isFinixAccount?: boolean;
}): ProcedureRow[] => {
  const internalRows: ProcedureRow[] = [];
  // FOR EACH PROCEDURE SELECTED IN AUTO COMPLETE
  if (!isEmpty(row)) {
    const { internalId, cptCode } = row;
    const rowsToAdd = rows.filter(
      (aRow) => aRow.internalId === internalId && aRow.cptCode === cptCode
    );
    const newRows = rowsToAdd.map((aRow) => {
      return {
        ...aRow,
        internalId: getNextInternalId(rows),
        id: getRandomNumber(aRow.providerProcedureId),
      };
    });

    internalRows.push(...newRows);
  } else {
    selectedProceduresObject.forEach((aSelectedProcedure, index) => {
      const providerProcedureId = getProcedureId(
        procedures,
        aSelectedProcedure
      );
      const providerProceduresInfo = getProviderProceduresInfo(
        providerProcedures,
        providerProcedureId
      );
      // FILTER UNIQUE PROVIDER PROCEDURES ROW
      const uniqueProviderProceduresRow = filterUniqueObjects(rows, [
        "internalId",
        "cptCode",
      ]);
      if (!uniqueProviderProceduresRow[index]) {
        let providerDropdown: ProviderDropdown[] = [];
        // FOR EACH PROVIDER PROCEDURE PREVIOUSLY FOUND
        providerProceduresInfo.forEach((providerProcedure) => {
          const providerSpecialty = providerProcedure?.provider.specialty;

          // CHECK IF EXISTS SAME SPECIALTY ADDED TO THE INTERNAL ROWS
          if (
            !isSpecialtyAlreadyAdded(
              internalRows,
              index,
              providerProcedureId,
              providerSpecialty
            )
          ) {
            // FIND ALL PROVIDERS WITH SAME SPECIALTY FROM THE DEFAULT PROVIDER PROCEDURE INFO
            const procedureProvidersWithSameSpecialty =
              getProcedureProvidersWithSameSpecialty(
                providerProceduresInfo,
                providerSpecialty
              );
            // FOR EACH PROVIDER WITH SAME SPECIALTY AND IF PRICE IS NOT 0
            // ADD TO THE PROVIDER DROPDOWN
            procedureProvidersWithSameSpecialty.forEach(
              (procedureProvider: {
                priceInCents: number;
                providerId: string;
                provider: {
                  name: string;
                  specialty: SpecialtyList;
                  finixMerchantId?: string | null;
                };
              }) => {
                if (isValidPrice(procedureProvider?.priceInCents)) {
                  const provider = procedureProvider?.provider;
                  providerDropdown.push({
                    id: procedureProvider?.providerId,
                    name: provider.name,
                    priceInCents: procedureProvider?.priceInCents,
                    specialty: provider.specialty,
                    // CLE-3074 — only when the search sent it: no key means "unknown", never "unlinked".
                    ...(provider && "finixMerchantId" in provider
                      ? { finixMerchantId: provider.finixMerchantId ?? null }
                      : {}),
                  });
                }
              }
            );
            // ADD TO THE INTERNAL ROWS, ASSUMING SELECTED PROVIDER IS THE FIRST ONE AND
            // AMOUNT IS THE FIRST ONE PRICE IN PROVIDERS DROPDOWN — on a Finix account the first
            // one whose payment setup is complete (CLE-3074), still the first one when none is.
            let selectedProvider = pickDefaultPayoutProvider(
              providerDropdown,
              isFinixAccount
            ) as ProviderDropdown;
            let selectedProviderProcedureId = providerProcedure?.id;
            let selectedAmount = selectedProvider.priceInCents;
            let selectedToBeEdited = providerProcedure?.toBeEdited;
            let selectedRequiresAbn = providerProcedure?.requiresAbn;
            let selectedRequiresAbnSelfPay =
              providerProcedure?.requiresAbnSelfPay;
            let selectedPreventive = providerProcedure?.preventive;

            // A default other than the first provider prices and flags the row from ITS
            // provider procedure, the way a provider picked from the dropdown does.
            if (selectedProvider !== providerDropdown[0]) {
              const defaultProviderProcedure = providerProceduresInfo.find(
                (pp) =>
                  pp.providerId === selectedProvider.id &&
                  pp.provider.specialty === providerSpecialty
              );
              if (defaultProviderProcedure) {
                selectedProviderProcedureId = defaultProviderProcedure.id;
                selectedToBeEdited = defaultProviderProcedure.toBeEdited;
                selectedRequiresAbn = defaultProviderProcedure.requiresAbn;
                selectedRequiresAbnSelfPay =
                  defaultProviderProcedure.requiresAbnSelfPay;
                selectedPreventive = defaultProviderProcedure.preventive;
              }
            }

            // If we have original procedures from a redo/draft/copy, try to match the provider
            // Note: This is only used for cases where all providers are already available
            // For RedoOrder/CopyOrder/ReviewOrder, matching happens after fetching all providers
            if (originalProcedures && originalProcedures.length > 0) {
              const matchingOriginalProcedure = originalProcedures.find(
                (origProc: any) =>
                  origProc.code?.toString() === aSelectedProcedure.code &&
                  origProc.specialty === providerSpecialty
              );

              if (matchingOriginalProcedure?.providerId) {
                // Find the matching provider in the dropdown
                const matchingProvider = providerDropdown.find(
                  (p) =>
                    p.id === matchingOriginalProcedure.providerId?.toString()
                );

                if (matchingProvider) {
                  selectedProvider = matchingProvider;
                  selectedAmount = matchingProvider.priceInCents;
                  // Try to find the matching providerProcedure for this provider
                  const matchingProviderProcedure = providerProceduresInfo.find(
                    (pp) =>
                      pp.providerId ===
                        matchingOriginalProcedure.providerId?.toString() &&
                      pp.provider.specialty === providerSpecialty
                  );
                  if (matchingProviderProcedure) {
                    selectedProviderProcedureId = matchingProviderProcedure.id;
                    selectedToBeEdited = matchingProviderProcedure.toBeEdited;
                    selectedRequiresAbn = matchingProviderProcedure.requiresAbn;
                    selectedRequiresAbnSelfPay =
                      matchingProviderProcedure.requiresAbnSelfPay;
                    selectedPreventive = matchingProviderProcedure.preventive;
                  }
                }
              }
            }

            // The fee schedule's defaults, in precedence order: Preventive, then
            // "Requires ABN + Self-Pay", then "Requires ABN".
            let defaultBenefitSelectionType: string | null = null;
            if (selectedPreventive) {
              defaultBenefitSelectionType = "PREVENTIVE";
            } else if (selectedRequiresAbnSelfPay) {
              defaultBenefitSelectionType =
                benefitSelectionTypes.SELF_PAY_WITH_ABN;
            } else if (selectedRequiresAbn) {
              defaultBenefitSelectionType = "REQUIRES_ABN";
            }

            internalRows.push({
              providerDropdown,
              internalId: index,
              specialty: providerSpecialty,
              selectedProvider,
              amount: selectedAmount,
              id: getRandomNumber(selectedProviderProcedureId),
              providerProcedureId: selectedProviderProcedureId,
              procedureId: providerProcedure?.procedureId,
              cptCode: aSelectedProcedure.code,
              description: aSelectedProcedure.cptDescription,
              quantity: 1,
              toBeEdited: selectedToBeEdited,
              preventive: selectedPreventive,
              benefitSelectionType: defaultBenefitSelectionType,
            });

            providerDropdown = [];
          }
        });
      }
    });
  }

  return internalRows;
};

export const buildFilteredProceduresSuggestions = (
  procedures: IProcedure[]
): string[] => {
  const parsedProcedures = procedures.map(
    (procedure: IProcedure) => `${procedure.cptCode} - ${procedure.description}`
  );
  return [...new Set(parsedProcedures)];
};

export const buildSelectedProceduresObject = (
  selectedProcedures: string[]
): {
  code: string;
  cptDescription: string;
}[] => {
  if (!selectedProcedures) return [];

  return selectedProcedures.map((selectedProcedures) => {
    const procedureCptCode = selectedProcedures.split(" ")[0].trim();
    const procedureDescription = selectedProcedures
      .split(" ")
      .slice(2)
      .join(" ");

    return {
      code: procedureCptCode,
      cptDescription: procedureDescription,
    };
  });
};

export const buildSimpleFirstColumnTemplate = (row: ProcedureRow) => {
  return <span className="font-bold">{row.providerName}</span>;
};

/**
 * CLE-2173: render a CPT code with its total RVU in parentheses (when known), e.g.
 * "30520 (RVU: 18.36)". RVU 0 / missing means we have no usable value, so we show
 * just the code.
 */
export const buildCptCodeWithRvu = (row: { cptCode: string; rvu?: number }) => {
  if (row.rvu && row.rvu > 0) {
    return `${row.cptCode} (RVU: ${row.rvu.toFixed(2)})`;
  }
  return row.cptCode;
};

export const buildSelectedProceduresArray = ({
  procedures,
}: {
  procedures: {
    code: number;
    providerProcedureId: number;
    specialty: string;
    cptDescription: string;
  }[];
}) => {
  interface IProcedureInfo {
    code: number;
    quantity: number;
    description: string;
  }
  const selectedProceduresInfo = procedures.reduce(
    (acc: Record<string, IProcedureInfo>, procedure) => {
      const key = `${procedure.code}-${procedure.providerProcedureId}-${procedure.specialty}`;
      if (!acc[key]) {
        acc[key] = {
          code: procedure.code,
          description: procedure.cptDescription,
          quantity: 0,
        };
      }
      acc[key].quantity += 1;
      return acc;
    },
    {}
  );

  const uniqueProcedures = uniqueByField(
    selectedProceduresInfo,
    "code"
  ) as IProcedureInfo[];

  const newProcedureArray = Object.entries(uniqueProcedures).flatMap(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([_key, value]) =>
      times(value?.quantity, () => `${value?.code} - ${value?.description}`)
  );

  return newProcedureArray;
};

export const buildDescriptionColumnTemplate = ({
  row,
  rows,
  dispatch,
  priorAuthByCode,
}: {
  row: ProcedureRow;
  rows: ProcedureRow[];
  dispatch: any;
  /** CLE-2766. Omitted by the non-GFE caller, which shows no prior-auth badges. */
  priorAuthByCode?: Record<string, IPriorAuthFinding>;
}) => {
  return (
    <ProcedureInfoColumn
      row={row}
      rows={rows}
      dispatch={dispatch}
      priorAuthFinding={priorAuthByCode?.[row.cptCode]}
    />
  );
};

export const buildSelectedProceduresPriceBreakdownToTable = ({
  priceBreakdown,
  dispatch,
}: {
  priceBreakdown: PricingBreakdown | undefined;
  dispatch: Dispatch;
}) => {
  const updatePatientResponsibilityState =
    priceBreakdown?.procedureBreakdown.map((aPrice) => {
      const { code, cptDescription, owedByPatient, providerName } = aPrice;
      const { coinsurance, deductible, selfPay } = owedByPatient;

      let description = "";
      if (coinsurance > 0 && deductible > 0) {
        description = "Deductible + Coinsurance";
      } else if (coinsurance > 0) {
        description = "Coinsurance";
      } else if (deductible > 0) {
        description = "Deductible";
      } else if (selfPay > 0) {
        description = "Self-Pay";
      }

      return {
        cptCode: code,
        description: cptDescription,
        amount: owedByPatient.total,
        specialty: description,
        providerName,
        // CLE-2173: RVU for this CPT, shown in parens next to the code on the estimate.
        rvu: priceBreakdown?.rvuByCode?.[code],
      };
    }) || [];

  const updateAdjustmentsState =
    priceBreakdown?.adjustments.map((adjustment) => {
      // Claim-level adjustment (e.g. CHARGE_BASED_PRICING_CAP) has no per-procedure shape.
      // Surface as a single per-provider row. The amount column shows the CAP itself (the
      // provider's post-cap subtotal) — matches how per-procedure rows show discountedPrice.
      // The explanation describes the raw subtotal and savings.
      if (!adjustment.procedure) {
        return {
          cptCode: "",
          description: "",
          amount: adjustment.maxAmountInCents ?? 0,
          specialty: adjustment.discountType,
          providerName: adjustment.providerName || "Provider Total",
          initialAmountInCents: adjustment.rawTotalInCents ?? 0,
          discountExplanation: adjustment.discountExplanation ?? "",
          hierarchyOverridden: false,
          rulesetId: adjustment.rulesetId,
          modifier: null,
          ncciPrimaryCptCode: undefined,
          paymentStats: undefined,
          rvu: undefined,
        };
      }
      return {
        cptCode: adjustment.procedure.code.toString(),
        description: adjustment.procedure.cptDescription || "",
        amount: adjustment.discountedPrice ?? 0,
        specialty: adjustment.discountType,
        providerName: adjustment.procedure.providerName,
        initialAmountInCents: adjustment.initialPrice ?? 0,
        discountExplanation: adjustment.discountExplanation,
        hierarchyOverridden: adjustment.hierarchyOverridden || false,
        rulesetId: adjustment.rulesetId,
        modifier: adjustment.modifier ?? null,
        ncciPrimaryCptCode: adjustment.ncciPrimaryCptCode,
        paymentStats: adjustment.paymentStats,
        // CLE-2173: RVU for this CPT, shown in parens next to the code on the estimate.
        rvu: priceBreakdown?.rvuByCode?.[adjustment.procedure.code],
      };
    }) || [];

  dispatch(
    setPriceBreakdownRows({
      adjustments: updateAdjustmentsState,
      patientResponsibility: updatePatientResponsibilityState,
    })
  );
};
